import { useTopicRead } from './useTopicRead';
import TopicWorkflowActions from './TopicWorkflowActions';
import { readSession, isAdminRole } from './messengerSession';
// Worker-event thread (#4005): the conversations inside one worker's event
// thread, listed by topic, plus one flat chronological view. Topic labels come
// from the server and are named after where each conversation started. A
// message sent from the flat view starts a ticket, a conversation with no record.

import { Box, Button, CircularProgress, Divider, IconButton, List, ListItemButton, ListItemText, Tooltip, Typography } from '@mui/material';
import ArrowBackTwoToneIcon from '@mui/icons-material/ArrowBackTwoTone';
import ForumTwoToneIcon from '@mui/icons-material/ForumTwoTone';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';

import ChatPaneV2 from './ChatPaneV2';
import { messageTopic, originTopicKey, topicHeading } from './eventThreadLinks';

import { useChatTopics, useTopicPreview } from 'src/queries/chat';

import type { Chat, ChatMessage, RecordSourceType, TopicContext, TopicSource } from 'src/models/chat';

type View =
  | { mode: 'topics' }
  | { mode: 'flat' }
  // `neutral`: the server label has not loaded, so the heading names only the kind of conversation.
  // `fromOrigin`: the record's sub-thread could not be previewed, so the first reply names the record.
  | { mode: 'topic'; key: string; label: string; neutral?: boolean; fromOrigin?: boolean };

interface EventThreadViewProps {
  chat: Chat;
  /** The record the thread was opened from (worker row, invoice row). */
  origin?: { type: RecordSourceType; id?: number } | null;
  initialTopicKey?: string | null;
  onOpenSource?: (message: ChatMessage, topic: { key: string; label: string; source?: TopicSource }) => void;
  compact?: boolean;
  initialAllMessages?: boolean;
}

export default function EventThreadView({ chat, origin, initialTopicKey, onOpenSource, compact, initialAllMessages }: EventThreadViewProps) {
  const { data: topics = [], isFetched, isError: topicsFailed } = useChatTopics(chat.id);
  const preview = useTopicPreview(chat.id, initialTopicKey ? null : origin);
  const [view, setView] = useState<View | null>(null);

  // Land on the topic the admin came from; otherwise list the conversations.
  useEffect(() => {
    setView(null);
  }, [chat.id, initialTopicKey, origin?.type, origin?.id]);
  useEffect(() => {
    if (view || !(isFetched || topicsFailed)) return;
    // Replies from a topic link or a record stay in that sub-thread even when its label cannot load;
    // only the All Messages view the admin chooses (or a thread with no conversations) starts a ticket.
    if (initialTopicKey) {
      const topic = topics.find(item => item.key === initialTopicKey);
      setView(topic
        ? { mode: 'topic', key: topic.key, label: topic.label }
        : { mode: 'topic', key: initialTopicKey, label: topicHeading(initialTopicKey), neutral: true });
      return;
    }
    if (origin) {
      if (preview.data) setView({ mode: 'topic', key: preview.data.key, label: preview.data.label });
      else if (preview.isError) {
        const key = originTopicKey(origin, chat.eventId);
        if (initialAllMessages) { setView({ mode: 'flat' }); return; }
    setView(topics.length ? { mode: 'topics' } : { mode: 'topic', key, label: topicHeading(key), neutral: true, fromOrigin: true });
      }
      return;
    }
    if (initialAllMessages) { setView({ mode: 'flat' }); return; }
    setView(topics.length ? { mode: 'topics' } : { mode: 'flat' });
  }, [view, isFetched, topicsFailed, topics, initialTopicKey, origin, preview.data, preview.isError, chat.eventId]);

  const topicKey = view?.mode === 'topic' ? view.key : null;
  const neutral = view?.mode === 'topic' && !!view.neutral;
  const fromOrigin = view?.mode === 'topic' && !!view.fromOrigin;
  const messageFilter = useCallback((message: ChatMessage) => messageTopic(message)?.key === topicKey, [topicKey]);
  const adminTopic = isAdminRole(readSession()?.roleName);
  const topicRead = useTopicRead(chat.id, adminTopic ? topicKey : null, topics.find(item => item.key === topicKey)?.readState);
  const started = !!topicKey && topics.some(item => item.key === topicKey);
  const previewKey = preview.data?.key;
  const sendContext = useMemo<TopicContext>(() => {
    if (!topicKey) return { ticket: {} };
    // Only the record's own sub-thread starts with a reply; every other topic, a new ticket included, continues by key.
    return origin && !started && (fromOrigin || topicKey === previewKey) ? { source: origin } : { topicKey };
  }, [topicKey, started, origin, fromOrigin, previewKey]);
  // A new ticket, or a sub-thread opened without its label, takes the server's name once a reply lands.
  const handleSent = useCallback((message?: ChatMessage) => {
    const topic = (!topicKey || neutral) && message ? messageTopic(message) : null;
    if (topic) setView({ mode: 'topic', key: topic.key, label: topic.label });
  }, [topicKey, neutral]);

  if (!view) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress size={24} /></Box>;
  }

  const allMessagesButton = (
    <Tooltip title="All Messages">
      <IconButton size="small" aria-label="All Messages" onClick={() => setView({ mode: 'flat' })} data-testid="event-thread-all-messages">
        <ForumTwoToneIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
  const header = view.mode === 'topics' ? (
    <Box display="flex" alignItems="center" justifyContent="space-between" pl={2} pr={1} py={1}>
      <Typography variant="h5">Conversations</Typography>
      {allMessagesButton}
    </Box>
  ) : (
    <Box display="flex" alignItems="center" gap={1} px={1} py={1}>
      <Tooltip title="Back to Conversations">
        <IconButton size="small" aria-label="Back to Conversations" onClick={() => setView({ mode: 'topics' })}>
          <ArrowBackTwoToneIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Typography variant="h5" sx={{ flex: 1, minWidth: 0 }} noWrap data-testid="event-thread-heading">
        {view.mode === 'topic' ? topics.find(item => item.key === view.key)?.label ?? view.label : 'All Messages'}
      </Typography>
      {view.mode === 'topic' && allMessagesButton}
    </Box>
  );

  // Messages and replies stay available when the topic list or label preview fails.
  const notice = topicsFailed
    ? 'Conversations could not load. Refresh the page.'
    : origin && preview.isError && view.mode !== 'topic'
      ? 'This record\'s conversation could not open. Refresh the page.'
      : null;

  return (
    <Box display="flex" flexDirection="column" flex={1} minHeight={0} data-testid="event-thread-view">
      {header}
      <Divider />
      {view.mode === 'topic' && isAdminRole(readSession()?.roleName) && topics.find(t => t.key === view.key) && <TopicWorkflowActions chatId={chat.id} topic={topics.find(t => t.key === view.key)!} />}

      {topicRead.error && <Box px={2}><Typography role="alert" variant="caption">Read status could not be saved.</Typography><Button size="small" onClick={topicRead.retry}>Retry</Button></Box>}
      {notice && (
        <Typography variant="body2" color="error" sx={{ px: 2, py: 1 }} role="status" data-testid="event-thread-notice">{notice}</Typography>
      )}
      {view.mode === 'topics' ? (
        <List disablePadding sx={{ overflow: 'auto', flex: 1 }} data-testid="event-thread-topics">
          {topics.map(topic => (
            <ListItemButton key={topic.key} onClick={() => setView({ mode: 'topic', key: topic.key, label: topic.label })}
              data-testid={`event-thread-topic-${topic.key}`}>
              <ListItemText
                primary={topic.label}
                primaryTypographyProps={{ variant: 'h5', noWrap: true }}
                secondary={topic.lastMessage?.content}
                secondaryTypographyProps={{ noWrap: true }}
              />
              <Box display="flex" flexDirection="column" alignItems="flex-end" ml={1} flexShrink={0}>
                <Typography variant="caption" color="text.secondary">{moment(topic.lastMessageAt).fromNow()}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {topic.messageCount === 1 ? '1 message' : `${topic.messageCount} messages`}
                </Typography>
              </Box>
            </ListItemButton>
          ))}
        </List>
      ) : (
        <ChatPaneV2
          chat={chat}
          onReadMessages={adminTopic ? topicRead.onReadMessages : undefined}
          messageFilter={view.mode === 'topic' ? messageFilter : undefined}
          sendContext={sendContext}
          onSent={handleSent}
          onOpenSource={onOpenSource}
          compact={compact}
        />
      )}
    </Box>
  );
}
